import { Routes } from '@angular/router';
import { AdminLayoutComponent } from './core/layout/admin-layout/admin-layout.component';
import { authGuard } from './features/auth/guards/auth.guard';
import { loginGuard } from './features/auth/guards/login.guard';
import {
  redirectToFirstPermittedChild,
  routePermissionGuard,
} from './features/auth/guards/route-permission.guard';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [loginGuard],
    loadComponent: () =>
      import('./features/auth/pages/login-page/login-page').then((m) => m.LoginPage),
  },
  {
    path: '',
    component: AdminLayoutComponent,
    canActivate: [authGuard],
    canActivateChild: [routePermissionGuard],
    children: [
      {
        path: 'no-access',
        loadComponent: () =>
          import('./features/auth/pages/no-access-page/no-access-page').then(
            (m) => m.NoAccessPage,
          ),
      },
      {
        path: '',
        loadComponent: () =>
          import('./features/dashboard/pages/dashboard-page/dashboard-page').then(
            (m) => m.DashboardPage,
          ),
      },
      {
        path: 'accounts',
        redirectTo: redirectToFirstPermittedChild('/accounts'),
        pathMatch: 'full',
      },
      {
        path: 'accounts/roles',
        loadComponent: () =>
          import('./features/accounts/pages/roles-page/roles-page').then(
            (m) => m.RolesPage,
          ),
      },
      {
        path: 'accounts/roles/new',
        loadComponent: () =>
          import('./features/accounts/pages/new-role-page/new-role-page').then(
            (m) => m.NewRolePage,
          ),
      },
      {
        path: 'accounts/roles/edit/:roleId',
        loadComponent: () =>
          import('./features/accounts/pages/edit-role-page/edit-role-page').then(
            (m) => m.EditRolePage,
          ),
      },
      {
        path: 'accounts/roles/view/:roleId',
        loadComponent: () =>
          import('./features/accounts/pages/view-role-page/view-role-page').then(
            (m) => m.ViewRolePage,
          ),
      },
      {
        path: 'accounts/roles/:roleId/users',
        loadComponent: () =>
          import('./features/accounts/pages/role-users-page/role-users-page').then(
            (m) => m.RoleUsersPage,
          ),
      },
      {
        path: 'accounts/users',
        loadComponent: () =>
          import('./features/accounts/pages/users-page/users-page').then(
            (m) => m.UsersPage,
          ),
      },
      {
        path: 'supervision',
        loadComponent: () =>
          import('./features/supervision/pages/supervision-page/supervision-page').then(
            (m) => m.SupervisionPage,
          ),
      },
      {
        path: 'news',
        loadComponent: () =>
          import('./features/news/pages/news-page/news-page').then(
            (m) => m.NewsPage,
          ),
      },
      {
        path: 'news/new',
        loadComponent: () =>
          import('./features/news/pages/add-news-page/add-news-page').then(
            (m) => m.AddNewsPage,
          ),
      },
      {
        path: 'news/edit/:id',
        loadComponent: () =>
          import('./features/news/pages/edit-news-page/edit-news-page').then(
            (m) => m.EditNewsPage,
          ),
      },
      {
        path: 'venues',
        loadComponent: () =>
          import('./features/venues/pages/venues-page/venues-page').then(
            (m) => m.VenuesPage,
          ),
      },
      {
        path: 'store',
        loadComponent: () =>
          import('./features/store/pages/category-list-page/category-list-page').then(
            (m) => m.CategoryListPage,
          ),
      },
      {
        path: 'store/:categoryId',
        loadComponent: () =>
          import('./features/store/pages/subcategory-list-page/subcategory-list-page').then(
            (m) => m.SubCategoryListPage,
          ),
      },
      {
        path: 'store/:categoryId/:subCategoryId',
        loadComponent: () =>
          import('./features/store/pages/branch-list-page/branch-list-page').then(
            (m) => m.BranchListPage,
          ),
      },
      {
        path: 'store/:categoryId/:subCategoryId/:branchId',
        loadComponent: () =>
          import('./features/store/pages/store-page/store-page').then(
            (m) => m.StorePage,
          ),
      },
      {
        path: 'store/:categoryId/:subCategoryId/:branchId/createProduct',
        loadComponent: () =>
          import('./features/store/pages/create-product-page/create-product-page').then(
            (m) => m.CreateProductPage,
          ),
      },
      {
        path: 'store/:categoryId/:subCategoryId/:branchId/product/:productId',
        loadComponent: () =>
          import('./features/store/pages/product-variants-page/product-variants-page').then(
            (m) => m.ProductVariantsPage,
          ),
      },
      {
        path: 'sales',
        loadComponent: () =>
          import('./features/sales/pages/sales-page/sales-page').then(
            (m) => m.SalesPage,
          ),
      },
      {
        path: 'fixture/players',
        loadComponent: () =>
          import('./features/content/pages/players-page/players-page').then((m) => m.PlayersPage),
      },
      {
        path: 'fixture/standings',
        loadComponent: () =>
          import('./features/content/pages/seasons-page/seasons-page').then(
            (m) => m.SeasonsPage,
          ),
      },
      {
        path: 'fixture/standings/:seasonId',
        loadComponent: () =>
          import('./features/fixture/pages/standings-page/standings-page').then(
            (m) => m.StandingsPage,
          ),
      },
      {
        path: 'fixture/configuration',
        loadComponent: () =>
          import('./features/content/pages/team-configuration-page/team-configuration-page').then(
            (m) => m.TeamConfigurationPage,
          ),
      },
      {
        path: 'events/sports',
        loadComponent: () =>
          import('./features/content/pages/sports-page/sports-page').then(
            (m) => m.SportsPage,
          ),
      },
      {
        path: 'events',
        loadComponent: () =>
          import('./features/events/pages/events-page/events-page').then(
            (m) => m.EventsPage,
          ),
      },
      {
        path: 'fixture',
        redirectTo: redirectToFirstPermittedChild('/fixture'),
        pathMatch: 'full',
      },
      {
        path: 'fixture/matches',
        loadComponent: () =>
          import('./features/fixture/pages/matches-page/matches-page').then(
            (m) => m.MatchesPage,
          ),
      },
      {
        path: 'tickets',
        loadComponent: () =>
          import('./features/tickets/pages/ticket-events-page/ticket-events-page').then(
            (m) => m.TicketEventsPage,
          ),
      },
      {
        path: 'tickets/attendees',
        loadComponent: () =>
          import('./features/tickets/pages/ticket-validation-page/ticket-validation-page').then(
            (m) => m.TicketValidationPage,
          ),
      },
      {
        path: 'tickets/used',
        loadComponent: () =>
          import('./features/tickets/pages/used-tickets-page/used-tickets-page').then(
            (m) => m.UsedTicketsPage,
          ),
      },
      {
        path: 'tickets/validation',
        loadComponent: () =>
          import('./features/tickets/pages/qr-validation-page/qr-validation-page').then(
            (m) => m.QrValidationPage,
          ),
      },
      {
        path: 'tickets/payment-vouchers',
        loadComponent: () =>
          import('./features/tickets/pages/payment-vouchers-page/payment-vouchers-page').then(
            (m) => m.PaymentVouchersPage,
          ),
      },
      {
        path: 'sales/vouchers',
        loadComponent: () =>
          import('./features/store/pages/order-vouchers-page/order-vouchers-page').then(
            (m) => m.OrderVouchersPage,
          ),
      },
      {
        path: 'members',
        loadComponent: () =>
          import('./features/members/pages/members-page/members-page').then(
            (m) => m.MembersPage,
          ),
      },
      {
        path: 'pop-ups',
        loadComponent: () =>
          import('./features/pop-ups/pages/pop-ups-page/pop-ups-page').then(
            (m) => m.PopUpsPage,
          ),
      },
      {
        path: 'sponsors',
        loadComponent: () =>
          import('./features/sponsors/pages/sponsors-page/sponsors-page').then(
            (m) => m.SponsorsPage,
          ),
      },
      {
        path: 'activity',
        loadComponent: () =>
          import('./features/activity/pages/activity-page/activity-page').then(
            (m) => m.ActivityPage,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/pages/settings-page/settings-page').then(
            (m) => m.SettingsPage,
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
